/* Seven dimensions in one cell, and the same seven as figures when a reader asks.
 *
 * **The strip exists because the figures do not scale.** Seven numeric columns are readable across
 * twenty rows and unreadable across five hundred; seven 14px cells in a fixed order are scannable
 * down any number of rows, and answer the question the figures cannot — *is consistency red across
 * the whole estate, or only here?* The expanded form is the same data for a reader who wants to
 * sort by one of them.
 *
 * The order is fixed and never sorted by value. A strip whose cells moved per row would make the
 * column meaningless: the point is that the fourth cell is consistency on every row.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { Dimension, Score } from '../api/scoring';
import { DIMENSION_ORDER, absence } from '../model/dimensions';
import { formatScore, scoreBand, worstDimension } from '../model/presentation';

export interface DimensionSummaryProps {
  /** `undefined` while the batched read is off or in flight — rendered as "not read", never as
   * seven absent dimensions, which would be a claim that nothing was measured. */
  readonly score: Score | undefined;
  readonly reading: boolean;
}

function cellClass(value: number | null, worst: boolean): string {
  const band = value === null ? 'absent' : scoreBand(value);
  const tone =
    band === 'absent'
      ? 'absent'
      : band === 'ok'
        ? 'ok'
        : band === 'warn'
          ? 'warn'
          : 'bad';
  return worst
    ? `lq-dstrip__cell lq-dstrip__cell--${tone} lq-dstrip__cell--worst`
    : `lq-dstrip__cell lq-dstrip__cell--${tone}`;
}

export function DimensionStrip({ score, reading }: DimensionSummaryProps): ReactElement {
  const { t } = useTranslation();

  if (score === undefined) {
    return (
      <span className="lq-muted" title={t('scoring.dimensions.unreadHint')}>
        {reading ? t('scoring.dimensions.reading') : t('scoring.dimensions.unread')}
      </span>
    );
  }

  const worst = worstDimension(score);
  const byDimension = new Map(score.dimensions.map((entry) => [entry.dimension, entry]));

  return (
    <span className="lq-dstrip">
      {DIMENSION_ORDER.map((dimension) => {
        const entry = byDimension.get(dimension);
        const value = entry?.value ?? null;
        const label = t(`scoring.dimension.${dimension}`);
        const title =
          value === null
            ? t('scoring.dimensions.cellAbsent', {
                dimension: label,
                reason: t(absence(entry, score)),
              })
            : t('scoring.dimensions.cellValue', {
                dimension: label,
                value: formatScore(value),
                weight: entry?.weight ?? 0,
              });
        return (
          <span
            key={dimension}
            className={cellClass(value, dimension === worst)}
            title={title}
            /* The strip as a whole is one graphic; each cell carries its own accessible name so a
               screen reader can walk the seven rather than hearing one opaque label. */
            role="img"
            aria-label={title}
          />
        );
      })}
    </span>
  );
}

/** One dimension's figure, for the expanded columns. */
export function DimensionFigure({
  score,
  dimension,
  reading,
}: {
  readonly score: Score | undefined;
  readonly dimension: Dimension;
  readonly reading: boolean;
}): ReactElement {
  const { t } = useTranslation();

  if (score === undefined) {
    return (
      <span className="lq-muted" aria-label={t('scoring.dimensions.unread')}>
        {reading ? '·' : '·'}
      </span>
    );
  }
  const entry = score.dimensions.find((candidate) => candidate.dimension === dimension);
  const value = entry?.value ?? null;
  if (value === null) {
    return (
      <span className="lq-scores__absent" title={t(absence(entry, score))}>
        {t('scoring.dimensions.noFigure')}
      </span>
    );
  }
  const worst = worstDimension(score) === dimension;
  return (
    <span
      className={
        worst ? 'lq-scores__figure lq-scores__figure--worst' : 'lq-scores__figure'
      }
    >
      {formatScore(value)}
    </span>
  );
}
